import { PageLayout } from   "@/components/layout/index";
import { usePageConfig } from "@/hooks/usePageConfig";
import { useOutletContext } from "react-router-dom";
import { useAppLayoutEvents } from "@/hooks/useAppLayoutEvents";
import { useMemo, useEffect } from "react";
import { usePageAccessGuard } from '@/hooks/usePageAccessGuard';

export default function HomePage() {
  usePageAccessGuard('home');
  // #region Context
  const { isEditMode, setIsEditMode } = useOutletContext<{
    isEditMode: boolean;
    setIsEditMode: (mode: boolean) => void;
  }>();
  // #endregion

  // Don't modify folder structure state when homepage loads
  // Let it persist from previous page so user can navigate back and forth
  // without losing their folder context
  
  // #region Default Config
  const defaultHomeConfig = useMemo(() => ({
    rows: [
      {
        id: 'default-row-1',
        columns: [
          {
            id: 'default-col-1',
            span: 12,
            elements: []
          }
        ]
      }
    ]
  }), []);
  // #endregion
  // #region Page Config
  const {
    pageLayoutConfig,
    isLoading,
    handlePageConfigChange,
    reloadConfig,
    LoadingComponent,
    NoConfigComponent,
  } = usePageConfig({
    sectionKey: "home",
    title: "Homepage",
    loadingMessage: "Loading homepage...",
    noConfigMessage: "No homepage configuration available",
    defaultConfig: defaultHomeConfig,
  }); 

  // #endregion
  // #region AppLayout Events
  const appLayoutDependencies = useMemo(() => [
    pageLayoutConfig, 
    handlePageConfigChange, 
    reloadConfig
  ], [pageLayoutConfig, handlePageConfigChange, reloadConfig]);

  const { shouldRenderEarly, earlyComponent } = useAppLayoutEvents({
    onSaveChanges: () => {
      if (pageLayoutConfig) {
        handlePageConfigChange(pageLayoutConfig);
      }
    },
    onCancelChanges: () => {
      reloadConfig();
    },
    dependencies: appLayoutDependencies,
    isLoading,
    pageLayoutConfig,
    LoadingComponent,
    NoConfigComponent,
  });
  // #endregion
  // #region Early Return for Loading/Error States
  if (shouldRenderEarly) {
    return earlyComponent;
  }
  // #endregion
  // #region Render
  return (
    <div className="h-full flex flex-col">
      <PageLayout
        config={pageLayoutConfig}
        onConfigChange={handlePageConfigChange}
        editMode={isEditMode}
        onEditModeChange={setIsEditMode}
        showTitle={false}
      />
    </div>
  );
  // #endregion
}
